import React from 'react';
import { PrimaryButton, Typography } from 'glints-aries/es/@next';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import {
  getJobCreatePath,
  getJobPostingAreaPathPrefixes,
} from '../../common/employerJobPaths';
import { getCurrentCompanyLoading } from '../../selectors/company';
import { getSessionCompany } from '../../selectors/session';
import { StyledLoading } from './CTAButtonLoggedIn.sc';
import { NavbarItem } from './NavbarItem';

type CTAButtonLoggedInProps = {
  fromJobEditPage?: boolean;
  fromOnboardingPage?: boolean;
  closeSidebar?(): void;
  compact?: boolean;
};

type NavbarButtonProps = {
  to: string;
  closeSidebar(): void;
  label: React.ReactNode;
  compact?: boolean;
};

const NavbarButton: React.FC<NavbarButtonProps> = ({
  to,
  closeSidebar,
  label,
  compact,
  ...menuItemProps
}) => {
  const buttonSize = compact ? 'slim' : 'default';
  const button = (
    <PrimaryButton
      size={buttonSize}
      style={{
        minHeight: 32,
        textTransform: 'uppercase',
      }}
    >
      <Typography variant="button">{label}</Typography>
    </PrimaryButton>
  );

  if (compact) {
    return (
      <Link to={to} onClick={closeSidebar}>
        {button}
      </Link>
    );
  }

  return (
    <NavbarItem
      to={to}
      onClick={closeSidebar}
      style={{ padding: 0 }}
      {...menuItemProps}
    >
      {button}
    </NavbarItem>
  );
};

const CTAButtons = {
  postJob: {
    label: (
      <FormattedMessage
        id="interactive-post-a-job"
        defaultMessage="Post A Job"
      />
    ),
  },
  createCompany: {
    label: (
      <FormattedMessage
        id="modules.Navigation.CTAButtonLoggedIn.create.a.company"
        defaultMessage="Create A Company"
      />
    ),
    to: '/onboarding',
  },
};

const shouldHidePostJobButton = (pathname: string) =>
  getJobPostingAreaPathPrefixes().some(prefix => pathname.startsWith(prefix));

const CTAButtonLoggedIn: React.FC<CTAButtonLoggedInProps> = ({
  closeSidebar = () => {
    void 0;
  },
  fromOnboardingPage,
  compact,
  ...menuItemProps
}) => {
  const hasCompany = Boolean(useSelector(getSessionCompany));
  const companyLoading = useSelector(getCurrentCompanyLoading);
  const { pathname } = useLocation();

  if (fromOnboardingPage) {
    return null;
  }

  if (hasCompany) {
    if (!shouldHidePostJobButton(pathname)) {
      return (
        <NavbarButton
          to={getJobCreatePath()}
          label={CTAButtons.postJob.label}
          closeSidebar={closeSidebar}
          compact={compact}
          {...menuItemProps}
        />
      );
    } else {
      return <></>;
    }
  } else if (companyLoading) {
    return <StyledLoading />;
  } else {
    return (
      <NavbarButton
        to={CTAButtons.createCompany.to}
        label={CTAButtons.createCompany.label}
        closeSidebar={closeSidebar}
        compact={compact}
        {...menuItemProps}
      />
    );
  }
};

export default CTAButtonLoggedIn;
